import Image from 'next/image'

import { stegaClean } from 'next-sanity'
import { PortableTextBlock, toPlainText } from 'next-sanity'

import { Button, ButtonWithDeref } from '@/app/components/Button'
import { Icon } from '@/app/components/Icon'
import PortableText from '@/app/components/PortableText'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Callout as CalloutRaw } from '@/sanity.types'
import { dataAttr, urlForImage } from '@/sanity/lib/utils'

export type CalloutProps = {
  callout: CalloutRaw & { _key?: string }
  tracking: TrackingLocation
  className?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

const alignmentClasses = {
  left: 'items-start text-left justify-start',
  center: 'items-center text-center justify-center',
  right: 'items-end text-right justify-end',
}

const buttonAlignmentClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}

const bgColorClasses = {
  'green-900': 'bg-green-900',
  'green-200': 'bg-green-200',
  'neutral-50': 'bg-neutral-50',
  'neon-200': 'bg-neon-200',
  'purple-600': 'bg-purple-700',
  'purple-300': 'bg-purple-300',
}

const iconBgColorClasses = {
  'green-300': 'bg-green-300',
  'green-600': 'bg-green-600',
  'purple-600': 'bg-purple-700',
  'neon-950': 'bg-neon-950',
  'white-default': 'bg-white-default',
}

const headingSizeClasses = {
  default: '!size-header-h5',
  large: '!size-header-h2 !-mb-lg',
}

const textSizeClasses = {
  default: 'size-body-xs',
  small: 'size-body-sm',
  medium: 'size-body-md',
  large: 'size-header-h6',
}

export const Callout = ({
  callout,
  tracking,
  className,
  documentId,
  documentType,
  parentPath,
}: CalloutProps) => {
  if (!callout) return null

  const alignment = stegaClean(callout.alignment) || 'left'
  const bgColor = stegaClean(callout.bgColor) || 'neutral-50'
  const iconBgColor = stegaClean(callout.iconBgColor) || 'green-300'
  const headingSize = stegaClean(callout.headingSize) || 'default'
  const textSize = stegaClean(callout.textSize) || 'default'
  const iconText = stegaClean(callout.iconText)

  // Force text colors based on background
  const headingColorOverride =
    bgColor === 'neon-200' || bgColor === 'green-200'
      ? '[&_p]:!text-white-950 [&_li]:!text-white-950 [&_svg]:!text-white-950'
      : bgColor === 'purple-600'
        ? '[&_p]:!text-white-default [&_li]:!text-white-default [&_svg]:!text-white-default'
        : bgColor === 'purple-300'
          ? '[&_p]:!text-white-950 [&_li]:!text-white-950 [&_svg]:!text-white-950'
          : ''

  const textColorOverride =
    bgColor === 'neon-200' || bgColor === 'green-200'
      ? '[&_p]:!text-white-950 [&_li]:!text-white-950 [&_svg]:!text-white-950'
      : bgColor === 'purple-600'
        ? '[&_p]:!text-white-default [&_li]:!text-white-default [&_svg]:!text-white-default'
        : bgColor === 'purple-300'
          ? '[&_p]:!text-white-900 [&_li]:!text-white-900 [&_svg]:!text-white-900'
          : ''

  // Icon color logic: green iconBgColor -> green-100 iconColor
  const iconColor =
    iconBgColor === 'green-300' || iconBgColor === 'green-600'
      ? 'text-green-100'
      : bgColor === 'purple-300' && iconBgColor === 'neon-950'
        ? 'text-neon-200'
        : bgColor === 'neon-200'
          ? 'text-neon-200'
          : bgColor === 'purple-600'
            ? 'text-purple-600'
            : 'text-white'

  const imageBaseUrl = urlForImage(callout.image)?.url()

  const attr =
    callout._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${callout._key}"]`,
        })
      : undefined

  return (
    <div
      className={cn(
        'flex flex-col gap-0 rounded-md overflow-hidden w-full max-w-[550px] mx-auto',
        alignmentClasses[alignment],
        bgColorClasses[bgColor],
        className,
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {callout.showImage && imageBaseUrl && (
        <Image
          loader={({ width }) => {
            return urlForImage(callout.image)?.width(width).url() || imageBaseUrl
          }}
          src={imageBaseUrl}
          alt={callout.heading ? toPlainText(callout.heading) : ''}
          width={310}
          height={310}
          className="h-auto w-full"
          sizes="(max-width: 768px) 100vw, 310px"
          loading="lazy"
        />
      )}

      <div className="flex flex-col gap-lg p-2xl my-0 mx-auto w-full h-full">
        {callout.showIcon && (callout.icon || iconText) && (
          <span
            className={cn(
              'flex items-center justify-center rounded-sm size-[46px] shrink-0 -mb-xs',
              iconBgColorClasses[iconBgColor],
            )}
          >
            {iconText ? (
              <span className={cn('size-header-h5', iconColor)}>{iconText}</span>
            ) : (
              <Icon icon={callout.icon as any} size={24} className={iconColor} />
            )}
          </span>
        )}

        {callout.heading && (
          <PortableText
            value={callout.heading as PortableTextBlock[]}
            tracking={tracking}
            className={headingColorOverride}
            paragraphClassName={cn(
              'text-white-950 !mb-0 line-clamp-3',
              alignment === 'center' && 'mx-auto',
              headingSizeClasses[headingSize as keyof typeof headingSizeClasses],
            )}
          />
        )}

        {callout.text && (
          <PortableText
            value={callout.text as PortableTextBlock[]}
            tracking={tracking}
            className={textColorOverride}
            paragraphClassName={cn(
              textSizeClasses[textSize as keyof typeof textSizeClasses],
              'text-white-800',
              alignment === 'center' && 'mx-auto',
            )}
          />
        )}

        {callout.showButton && callout.button && (
          <div className="mt-auto">
            <Button
              button={
                {
                  ...callout.button,
                  variant: 'primaryDark',
                  size: 'sm',
                } as ButtonWithDeref
              }
              tracking={tracking}
              className={buttonAlignmentClasses[alignment]}
            />
          </div>
        )}
      </div>
    </div>
  )
}
